import type { BackOfficeMessageKey } from '@eklevo/shared/i18n/backoffice'
import { Chip } from '../../components/ui/chip'
import { cn } from '../../lib/cn'
import { useI18n } from '../lib/i18n'
import type { CatalogCourse, Company, CompanyDetail, CompanyStatus } from '../lib/screens'

/*
 * The pieces of a company and a course as the back office shows them (ADR 0008 §3): names,
 * states and counts. Never a logo or colour of a company's (its brand is the portal's, ADR 0007),
 * never a person. Not in the Figma's detail: marked for design review.
 */

/** The first letters of a company's first two words, whole even when one takes two code units. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/u).filter(Boolean)
  return words
    .slice(0, 2)
    .map((w) => Array.from(w)[0] ?? '')
    .join('')
    .toLocaleUpperCase()
}

/** A company's initials in a square, as the Figma's cards (86:2451). Decorative: the name is beside it. */
export function CompanyInitials({ name, className }: { name: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-lg bg-page text-sm font-medium text-ink-muted',
        className,
      )}
    >
      {initials(name)}
    </span>
  )
}

const ORGANIZATION_TONE = { active: 'success', suspended: 'danger' } as const

export function OrganizationStatus({ status }: { status: CompanyStatus }) {
  const { t } = useI18n()
  return <Chip tone={ORGANIZATION_TONE[status]}>{t(`status.organization.${status}`)}</Chip>
}

/** As the HR console shows a program's state (its chips' tones). */
const PROGRAM_TONE = {
  not_launched: 'muted',
  active: 'dark',
  completed: 'success',
  stopped: 'light',
} as const

export function ProgramStatus({ status }: { status: CompanyDetail['courses'][number]['status'] }) {
  const { t } = useI18n()
  return <Chip tone={PROGRAM_TONE[status]}>{t(`status.program.${status}`)}</Chip>
}

const COURSE_TONE = { draft: 'light', published: 'dark', archived: 'muted' } as const

export function CourseStatus({ status }: { status: CatalogCourse['status'] }) {
  const { t } = useI18n()
  return <Chip tone={COURSE_TONE[status]}>{t(`status.course.${status}`)}</Chip>
}

/** The counts of a company (ADR 0008 §3), in this order. */
const COUNT_LABELS = [
  ['activePeople', 'company.activePeople'],
  ['invitedPeople', 'company.invitedPeople'],
  ['hrPeople', 'company.hrPeople'],
  ['departments', 'company.departments'],
  ['activeCourses', 'company.activeCourses'],
] as const satisfies readonly (readonly [keyof Company, BackOfficeMessageKey])[]

/**
 * A company's counts as a list of terms and values: people active and invited, its HR team, its
 * departments and its active courses. Numbers only, in the page's language.
 */
export function CompanyCounts({ company, className }: { company: Company; className?: string }) {
  const { t, locale } = useI18n()
  const number = new Intl.NumberFormat(locale)
  return (
    <dl className={cn('grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3', className)}>
      {COUNT_LABELS.map(([field, label]) => (
        <div key={field} className="min-w-0">
          <dt className="text-xs text-ink-muted">{t(label)}</dt>
          <dd className="text-lg font-medium tabular-nums">{number.format(company[field])}</dd>
        </div>
      ))}
    </dl>
  )
}
